import React, { useState } from 'react';
import { toast } from 'sonner';
import { X, FileArrowUp, CheckCircle } from '@phosphor-icons/react';

const GROUPS = [
  { id: 'breakfast', label: 'Breakfast rotation', emoji: '🍳' },
  { id: 'lunch', label: 'Lunch rotation', emoji: '🍛' },
  { id: 'dinner', label: 'Dinner rotation', emoji: '🌙' },
  { id: 'snack', label: 'Hi-tea / Snack rotation', emoji: '☕' },
  { id: 'extras', label: 'Starters, sides & soups → liked dishes', emoji: '🥗' },
  { id: 'kids_lunch', label: "Kids Dabba rotation (both kids)", emoji: '🍱' },
];

const headerGroup = (h) => {
  const s = String(h || '').toLowerCase();
  if (!s) return null;
  if (s.includes('kid')) return 'kids_lunch';
  if (s.includes('breakfast')) return 'breakfast';
  if (s.includes('dinner')) return 'dinner';
  if (s.includes('snack')) return 'snack';
  if (s.includes('lunch')) return 'lunch';
  if (/starter|farsan|side|salad|soup/.test(s)) return 'extras';
  return null;
};

const cleanItem = (v) => {
  const s = String(v ?? '').trim();
  if (!s || s.length > 60 || s.includes(':')) return '';
  return s.replace(/\s+/g, ' ').toLowerCase().replace(/(^|\s|\/)\w/g, c => c.toUpperCase());
};

const gridToGroups = (grid) => {
  let headerIdx = -1, mapping = null;
  for (let i = 0; i < Math.min(grid.length, 5); i++) {
    const m = {};
    (grid[i] || []).forEach((cell, j) => { const g = headerGroup(cell); if (g && m[g] === undefined) m[g] = j; });
    if (Object.keys(m).length >= 2) { headerIdx = i; mapping = m; break; }
  }
  if (!mapping) return null;
  const groups = {};
  Object.entries(mapping).forEach(([g, col]) => {
    const seen = new Set();
    groups[g] = [];
    for (let i = headerIdx + 1; i < grid.length; i++) {
      const item = cleanItem((grid[i] || [])[col]);
      if (item && !seen.has(item.toLowerCase())) { seen.add(item.toLowerCase()); groups[g].push(item); }
    }
    if (groups[g].length === 0) delete groups[g];
  });
  return Object.keys(groups).length ? groups : null;
};

export default function ImportItemsModal({ api, onClose, onUpdate }) {
  const [groups, setGroups] = useState(null);
  const [include, setInclude] = useState({});
  const [applying, setApplying] = useState(false);

  const loadGrid = (grid) => {
    const g = gridToGroups(grid);
    if (!g) { toast.error('Could not find category columns (Breakfast / Lunch / Dinner / Snack / Kids Lunch…)'); return; }
    setGroups(g);
    setInclude(Object.fromEntries(Object.keys(g).map(k => [k, true])));
  };

  const handleFile = async (file) => {
    if (!file) return;
    try {
      if (/\.(xlsx|xls)$/i.test(file.name)) {
        const XLSX = await import('xlsx');
        const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
        // Use the sheet with the most category headers (usually "All Items List")
        let best = null, bestCount = 0;
        for (const sn of wb.SheetNames) {
          const grid = XLSX.utils.sheet_to_json(wb.Sheets[sn], { header: 1, raw: false });
          const g = gridToGroups(grid);
          const count = g ? Object.keys(g).length : 0;
          if (count > bestCount) { best = grid; bestCount = count; }
        }
        if (!best) { toast.error('No items-list sheet found in this file'); return; }
        loadGrid(best);
      } else {
        const text = await file.text();
        loadGrid(text.split(/\r?\n/).map(l => l.includes('\t') ? l.split('\t') : l.split(',')));
      }
    } catch (e) { console.error(e); toast.error('Could not read that file'); }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const payload = {};
      Object.entries(groups).forEach(([k, items]) => { if (include[k]) payload[k] = items; });
      payload.apply_kids = !!(include.kids_lunch && groups.kids_lunch);
      const r = await api.post('/meals/food-profile/import', payload);
      const a = r.data.added || {};
      const totalNew = Object.values(a).reduce((s, n) => s + n, 0);
      const kidBits = Object.entries(r.data.kids_rotation_added || {}).map(([n, c]) => `${n}: +${c}`).join(', ');
      toast.success(`Merged ${totalNew} new item(s) into the Food Profile${kidBits ? ` · Dabba (${kidBits})` : ''} ✅`);
      onUpdate();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    setApplying(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-2xl p-5 max-h-[85vh] overflow-y-auto" data-testid="import-items-modal"
        style={{ background: '#16181d', border: '1px solid rgba(255,255,255,0.1)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-white font-bold text-base flex items-center gap-2"><FileArrowUp size={18} className="text-[#FF6B35]" /> Import Items List</h2>
          <button onClick={onClose} data-testid="import-items-close" className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-white/5"><X size={16} /></button>
        </div>
        <p className="text-xs text-neutral-500 mb-4">
          Upload your family food Excel (columns like BREAKFAST, LUNCH, DINNER, SNACK, KIDS LUNCH…). Everything <span className="text-emerald-300 font-semibold">merges</span> into your profile — nothing existing is removed.
        </p>

        {!groups ? (
          <label className="block rounded-2xl border-2 border-dashed border-white/15 hover:border-[#FF6B35]/50 transition-colors p-8 text-center cursor-pointer" data-testid="import-items-drop">
            <FileArrowUp size={28} className="text-neutral-500 mx-auto mb-2" />
            <p className="text-sm text-neutral-300 font-semibold">Choose .xlsx / .csv file</p>
            <p className="text-[11px] text-neutral-600 mt-1">The best-matching sheet is picked automatically</p>
            <input type="file" accept=".xlsx,.xls,.csv,.tsv" className="hidden" data-testid="import-items-file"
              onChange={e => handleFile(e.target.files?.[0])} />
          </label>
        ) : (
          <div className="space-y-3">
            {GROUPS.filter(g => groups[g.id]).map(g => (
              <div key={g.id} className="rounded-xl p-3" data-testid={`import-group-${g.id}`}
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <input type="checkbox" checked={!!include[g.id]} data-testid={`import-group-check-${g.id}`}
                    onChange={e => setInclude(prev => ({ ...prev, [g.id]: e.target.checked }))} className="accent-[#FF6B35]" />
                  <span className="text-xs font-bold text-white">{g.emoji} {g.label}</span>
                  <span className="text-[10px] text-neutral-500">({groups[g.id].length} items)</span>
                </label>
                <div className={`flex flex-wrap gap-1 ${include[g.id] ? '' : 'opacity-30'}`}>
                  {groups[g.id].map((it, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[10px] text-neutral-300">{it}</span>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setGroups(null)} className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-neutral-300 text-xs font-bold">Choose another file</button>
              <button onClick={apply} disabled={applying || !Object.values(include).some(Boolean)} data-testid="import-items-apply"
                className="px-5 py-2 rounded-xl bg-[#FF6B35] text-white text-xs font-bold disabled:opacity-50 flex items-center gap-1.5">
                <CheckCircle size={14} weight="fill" /> {applying ? 'Merging…' : 'Merge into profile'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
